import { cleanRun, IRichParagraph, IRichRun, mergeRuns } from './richTextModel';

/**
 * Urkundentext ↔ Editor-Dokument (#1245, Teil 48).
 *
 * Der Texteditor ist ein einziges Bearbeitungsfeld je Element (Tiptap/ProseMirror), nicht mehr
 * ein Kasten mit eigener Werkzeugleiste je Absatz. Bei einem geklonten Abschnitt mit 37 Absätzen
 * brauchte der alte Editor 7,5 Sekunden, um sich zu öffnen (542 Eingabefelder).
 *
 * Gespeichert wird weiter nie HTML und nie das Editor-Dokument, sondern `IRichParagraph[]`.
 * Dieses Modul übersetzt verlustfrei in beide Richtungen und kennt Tiptap nicht — so ist es
 * ohne Browser testbar (Jest übersetzt `@tiptap/pm` nicht).
 *
 *   Absatz          → `paragraph` mit dem ganzen Absatzformat als Attribut `format`
 *                     (auch übernommene Einzüge, Zeilenabstände, Rahmen, `continues`)
 *   Textlauf        → Text mit Marken `bold`, `italic`, `underline`, `runStyle`
 *   Zeilenumbruch   → `hardBreak`
 *   Datenfeld       → `dataField` (Atom: davor und dahinter schreiben, als Ganzes löschen)
 *   bedingter Text  → `inlineChoice` (Atom)
 *   offene Stelle   → `openSpot` (Atom)
 */

export interface DocMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface DocNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
  text?: string;
  marks?: DocMark[];
}

/** Alles am Absatz außer seinem Inhalt. */
export type ParagraphFormat = Omit<IRichParagraph, 'runs'>;

/** Direktformatierung eines Laufs, die kein eigenes Bedienelement hat — sie reist nur mit. */
export type RunStyle = Pick<
  IRichRun,
  'fontFamily' | 'fontSize' | 'color' | 'underlineStyle'
>;

/** Formatierung eines Atoms (ein fettes Datenfeld bleibt fett). */
export type AtomFormat = Pick<
  IRichRun,
  | 'bold'
  | 'italic'
  | 'underline'
  | 'fontFamily'
  | 'fontSize'
  | 'color'
  | 'underlineStyle'
>;

const runStyleOf = (run: IRichRun): RunStyle => {
  const style: RunStyle = {};
  if (run.fontFamily) style.fontFamily = run.fontFamily;
  if (run.fontSize !== undefined && run.fontSize !== null)
    style.fontSize = run.fontSize;
  if (run.color) style.color = run.color;
  if (run.underlineStyle) style.underlineStyle = run.underlineStyle;
  return style;
};

const atomFormatOf = (run: IRichRun): AtomFormat => {
  const format: AtomFormat = { ...runStyleOf(run) };
  if (run.bold) format.bold = true;
  if (run.italic) format.italic = true;
  if (run.underline) format.underline = true;
  return format;
};

const marksOf = (run: IRichRun): DocMark[] => {
  const marks: DocMark[] = [];
  if (run.bold) marks.push({ type: 'bold' });
  if (run.italic) marks.push({ type: 'italic' });
  if (run.underline) marks.push({ type: 'underline' });
  const style = runStyleOf(run);
  if (Object.keys(style).length > 0) {
    marks.push({ type: 'runStyle', attrs: { style } });
  }
  return marks;
};

const inlineOf = (run: IRichRun): DocNode | null => {
  if (run.lineBreak) {
    return { type: 'hardBreak' };
  }
  if (run.selector) {
    return {
      type: 'dataField',
      attrs: {
        selector: run.selector,
        label: run.label ?? null,
        format: atomFormatOf(run),
      },
    };
  }
  if (run.choice) {
    return {
      type: 'inlineChoice',
      attrs: { choice: run.choice, format: atomFormatOf(run) },
    };
  }
  if (run.migration) {
    return {
      type: 'openSpot',
      attrs: { migration: run.migration, format: atomFormatOf(run) },
    };
  }
  if (!run.text) {
    return null;
  }
  const marks = marksOf(run);
  return marks.length > 0
    ? { type: 'text', text: run.text, marks }
    : { type: 'text', text: run.text };
};

export const paragraphsToDoc = (paragraphs: IRichParagraph[]): DocNode => ({
  type: 'doc',
  content: (paragraphs.length > 0 ? paragraphs : [{ runs: [] }]).map(
    paragraph => {
      const { runs, ...format } = paragraph;
      const content = (runs ?? [])
        .map(inlineOf)
        .filter((node): node is DocNode => node !== null);
      return content.length > 0
        ? { type: 'paragraph', attrs: { format }, content }
        : { type: 'paragraph', attrs: { format } };
    }
  ),
});

const runFromText = (node: DocNode): IRichRun => {
  const run: IRichRun = { text: node.text ?? '' };
  (node.marks ?? []).forEach(mark => {
    if (mark.type === 'bold') run.bold = true;
    if (mark.type === 'italic') run.italic = true;
    if (mark.type === 'underline') run.underline = true;
    if (mark.type === 'runStyle') {
      Object.assign(run, (mark.attrs?.style ?? {}) as RunStyle);
    }
  });
  return run;
};

const runFromInline = (node: DocNode): IRichRun | null => {
  const attrs = node.attrs ?? {};
  const format = (attrs.format ?? {}) as AtomFormat;
  switch (node.type) {
    case 'text':
      return runFromText(node);
    case 'hardBreak':
      return { lineBreak: true };
    case 'dataField':
      return {
        ...format,
        selector: String(attrs.selector ?? ''),
        label: (attrs.label as string | null | undefined) ?? null,
      };
    case 'inlineChoice':
      return { ...format, choice: attrs.choice as IRichRun['choice'] };
    case 'openSpot':
      return { ...format, migration: attrs.migration as IRichRun['migration'] };
    default:
      return null;
  }
};

export const docToParagraphs = (doc: DocNode): IRichParagraph[] => {
  const paragraphs = (doc.content ?? [])
    .filter(node => node.type === 'paragraph')
    .map(node => {
      const format = { ...((node.attrs?.format ?? {}) as ParagraphFormat) };
      const runs = (node.content ?? [])
        .map(runFromInline)
        .filter((run): run is IRichRun => run !== null);
      return { ...format, runs: mergeRuns(runs).map(cleanRun) };
    });
  return paragraphs.length > 0 ? paragraphs : [{ runs: [] }];
};

/**
 * Formatänderung an einem Absatz (Werkzeugleiste, »Absatzformat …«). Was Word bei einem Absatztyp
 * aus der Vorlage nimmt, fällt beim Setzen des Typs nicht still weg — es bleibt gespeichert und
 * wirkt wieder, wenn der Typ zurückgenommen wird.
 */
export const patchFormat = (
  format: ParagraphFormat,
  patch: Partial<ParagraphFormat>
): ParagraphFormat => ({ ...format, ...patch });

/**
 * Absatzformat nach »Eingabe«, wie in Word:
 *  - der erste Teil endet — er setzt nicht mehr im nächsten Knoten fort (`continues` gehört dem
 *    letzten Teil),
 *  - der neue Teil beginnt keine neue Seite,
 *  - nach einer Überschrift am Absatzende geht es mit Fließtext weiter.
 */
export const splitFormats = (
  format: ParagraphFormat,
  atEnd: boolean
): { first: ParagraphFormat; second: ParagraphFormat } => {
  const heading =
    format.style === 'heading1' ||
    format.style === 'heading2' ||
    format.style === 'heading3';
  const first: ParagraphFormat = { ...format };
  delete first.continues;
  const second: ParagraphFormat = { ...format };
  delete second.pageBreakBefore;
  if (heading && atEnd) {
    second.style = null;
  }
  return { first, second };
};
